/**
 * Metering & entitlements foundation — the declarative metric registry + the
 * single entitlement resolver. Canonical design: docs/billing/metering-and-entitlements.md.
 *
 * This is the *evolutive* surface: adding a future quota = ONE entry in
 * METRIC_REGISTRY (§15 of the doc). The gate, admin form, dashboard, and Stripe
 * pack reconstruction all derive from this map.
 *
 * Phase 1 scope (current): the registry + resolveEntitlement, with defaultByTier
 * mirroring the legacy TIER_LIMITS for the overlapping metrics so there is zero
 * behavior change. Enforcement wiring (gauges/meters/reserve/commit) lands in
 * later phases — see the doc's §17.
 */

import type { Tier } from './tenant';
import type { Cadence } from './cadence';

/** The five enforcement shapes. Every metric is exactly one. See doc §2. */
export type MetricKind = 'gauge' | 'meter' | 'allowlist' | 'scalar' | 'boolean';

export interface MetricDef {
  /** Stable key — also the Stripe pack `metadata.metric` value. Never rename in place. */
  key: string;
  kind: MetricKind;

  /** Meters only: counter reset period. 'balance' = no reset (credits). */
  period?: 'day' | 'month' | 'balance';

  /**
   * Default limit per tier. Semantics by kind:
   *  - gauge / meter / scalar: a number. -1 = unlimited (still clamped by systemCeiling).
   *  - allowlist: the `max` count; the permitted set default is `allowlistDefaults`.
   *  - boolean: 0 = off, 1 = on.
   */
  defaultByTier: Record<Tier, number>;

  /** allowlist only: the default permitted set per tier. */
  allowlistDefaults?: Partial<Record<Tier, string[]>>;

  /**
   * Hard ceiling, tier-independent. Effective limit is always
   * min(tierDefault + packs + override, systemCeiling). Protects the app from
   * runaway/misconfigured limits. -1 only if genuinely unbounded.
   */
  systemCeiling: number;

  /** Can upsell packs grant more of this? (gauge/meter only.) */
  addonStackable: boolean;

  unit: string;
  label: { fr: string; en: string };

  /** Dashboard / admin-form grouping. */
  group?: string;
}

// Primary = real consumer products (registry name `chatgpt` ↔ run-path `openai`).
const ALL_ENGINES = ['chatgpt', 'gemini', 'claude', 'perplexity'];
// Secondary = simulated engines.  Per-tier allowlists (Prompt Studio C17.1, 2026-10-15):
//   basic  → chatgpt + ai_overview (2 engines)
//   pro    → chatgpt + ai_overview + perplexity + gemini (4 engines)
//   elite  → ALL_ENGINES + mistral + ai_overview + google = 7 engines
//   enterprise / agency → configurable (unchanged, selectable from all secondaries)
// Secondary ids (same in registry and run-path, no alias):
//   mistral       → Bedrock Mistral + Serper search loop ≈ Le Chat
//   ai_overview   → Serper SERP + page-1 <main> + Gemini ≈ Google AI Overview
//   google_ai_mode → Google AI Overview secondary surface
//   google        → CONVERSATIONS ONLY — no BP `google` engine; present here so
//                   ConversationsService gates `google` cells (inert for BP pickers).
// See docs/runbooks/adding-bp-engines.md.
const SECONDARY_ENGINES = ['mistral', 'ai_overview', 'google_ai_mode', 'google'];
const ALL_ENGINES_WITH_SECONDARY = [...ALL_ENGINES, ...SECONDARY_ENGINES];
// Prompt Studio per-tier engine allowlists (C17.1, 2026-10-15).
const ENGINES_BASIC = ['chatgpt', 'ai_overview'];
const ENGINES_PRO   = ['chatgpt', 'ai_overview', 'perplexity', 'gemini'];
const ENGINES_ELITE = [...ALL_ENGINES, 'mistral', 'ai_overview', 'google']; // 7

/**
 * The metric registry. Numbers mirror the agreed 5-tier table (basic as the
 * floor, monotonic up, enterprise/agency unlimited or ceiling). The authoritative
 * commercial values live in docs/billing/pricing-and-billing.md §2; keep in sync.
 */
export const METRIC_REGISTRY: Record<string, MetricDef> = {
  // ---------------- gauges (stock — count of live things) ----------------
  brands: {
    key: 'brands', kind: 'gauge', addonStackable: true, unit: 'marques',
    // Pricing v2 (2026-11-04): UI « Espaces de travail ». Enterprise = Elite
    // default, raised per deal through quotaOverrides — never -1.
    defaultByTier: { basic: 1, pro: 2, elite: 5, enterprise: 5, agency: -1 },
    systemCeiling: 200, label: { fr: 'Espaces de travail', en: 'Workspaces' }, group: 'identity', // UI name of a Brand (O11)
  },
  // Counted TENANT-WIDE (shared pool across all brands). systemCeiling is the
  // tenant-wide runaway cap, not a per-brand limit — at 50 it was clamping
  // agency/enterprise (-1) to 50 zones across their ENTIRE book of brands.
  zones: {
    key: 'zones', kind: 'gauge', addonStackable: true, unit: 'zones',
    // Retired as a commercial limit on every tier (pricing v2, 2026-11-04);
    // systemCeiling still clamps.
    defaultByTier: { basic: -1, pro: -1, elite: -1, enterprise: -1, agency: -1 },
    systemCeiling: 1000, label: { fr: 'Zones', en: 'Zones' }, group: 'identity',
  },
  languages: {
    key: 'languages', kind: 'gauge', addonStackable: true, unit: 'langues',
    // Retired as a commercial limit (pricing v2, 2026-11-04); systemCeiling clamps.
    defaultByTier: { basic: -1, pro: -1, elite: -1, enterprise: -1, agency: -1 },
    systemCeiling: 20, label: { fr: 'Langues', en: 'Languages' }, group: 'identity',
  },
  // Shared across the 3 BP axes (supersedes the per-axis query caps; the gauge
  // is wired into the create path in metering Phase 2).
  // NOTE: from Prompt Studio ship 1 onward, no Brand Perception prompt path asserts
  // this gauge (U2, 2026-10-15).  It remains for the Product module (Audience).
  prompts: {
    key: 'prompts', kind: 'gauge', addonStackable: true, unit: 'prompts',
    // Agency is credit-metered (real spend bounded by the credit balance); the
    // 1000 here is only a SOFT runaway-prevention cap, not a commercial limit —
    // raise it per-agency via quotaOverrides on request.
    defaultByTier: { basic: 30, pro: 100, elite: 200, enterprise: -1, agency: 1000 },
    systemCeiling: 2000, label: { fr: 'Prompts', en: 'Prompts' }, group: 'brand_perception',
  },
  // ---- Prompt Studio (C18 / 00b DQ10, 2026-10-15) --------------------------
  // PER-BRAND monthly allowance: 1 prompt × 1 engine × 1 launch = 1 request.
  // Zone/language variants count as separate prompts.  Read ONLY through
  // `resolveBrandAllowances(tenant, brand)` (brand-allowances.ts).
  // ⚠ Never sum across brands — the admin UI shows the MAX.  systemCeiling -1
  // so admin overrides are never silently clamped (the allowance check in C18
  // has its own logic).  A trial tenant = basic.
  brand_prompt_requests: {
    key: 'brand_prompt_requests', kind: 'scalar', addonStackable: false, unit: 'demandes',
    // Enterprise = Elite default (pricing v2, 2026-11-04): an unset override must
    // not read as unlimited on a spend-bearing metric.
    defaultByTier: { basic: 650, pro: 3000, elite: 8000, enterprise: 8000, agency: -1 },
    systemCeiling: -1,
    label: { fr: 'Demandes de prompts par mois (par marque)', en: 'Prompt requests per month (per brand)' },
    group: 'brand_perception',
  },
  // PER-BRAND structural cap on active prompt rows (classified + pending, any axis).
  // NOT a quota; never shown as one; never enforced at dispatch.  Bounds the launch
  // planner's page count and per-prompt chunks (00b DQ14).  Ceiling 7 500 so the
  // planner can persist one launch selection in ≤ 100-item TransactWriteItems
  // (4 axes × 6 engines × 7 500 / one message per run ≤ 94 pages + META).
  brand_prompts_max: {
    key: 'brand_prompts_max', kind: 'scalar', addonStackable: false, unit: 'prompts',
    defaultByTier: { basic: 5000, pro: 5000, elite: 5000, enterprise: 5000, agency: 5000 },
    systemCeiling: 7500,
    label: { fr: 'Prompts actifs max (par marque)', en: 'Max active prompts (per brand)' },
    group: 'brand_perception',
  },
  // Counted TENANT-WIDE (shared pool across all brands), like prompts/zones.
  // systemCeiling is the tenant-wide runaway cap — it must NOT be the per-brand
  // matrix DOM cap (that is COMPETITOR_HARD_CAP=100, enforced separately per
  // brand in CompetitorsService.add), otherwise agency/enterprise (-1) would be
  // clamped to 100 competitors across ALL brands.
  competitors: {
    key: 'competitors', kind: 'gauge', addonStackable: true, unit: 'concurrents',
    defaultByTier: { basic: 3, pro: 10, elite: 25, enterprise: 25, agency: -1 },
    systemCeiling: 1000, label: { fr: 'Concurrents suivis', en: 'Tracked competitors' }, group: 'competitors',
  },
  users: {
    key: 'users', kind: 'gauge', addonStackable: true, unit: 'utilisateurs',
    // Pricing v2 D4 (2026-11-04): Starter 2 / Pro 5 / Elite 10 (Enterprise = Elite);
    // raised per tenant via quotaOverrides.users.
    defaultByTier: { basic: 2, pro: 5, elite: 10, enterprise: 10, agency: 10 },
    systemCeiling: 500, label: { fr: 'Utilisateurs', en: 'Users' }, group: 'identity',
  },
  // Product module — INDEXED CATALOGUE CAPACITY: the sum of persisted items
  // (products + category pages) across EVERY catalogue of EVERY brand of the
  // tenant. It's the capacity the module is sold on, so it's a gauge, not a
  // meter: deleting a catalogue frees the slots back.
  //
  // Every tier defaults to 0 — the Product module is admin-gated
  // (featureFlags.productModuleEnabled), so capacity is bought (a self-serve
  // `catalog_items` pack) or negotiated (quotaOverrides.catalog_items for
  // enterprise). Agency is credit-metered on the RUNS, so its capacity is
  // deliberately not the throttle — CATALOGUE_MAX_ITEMS (50k/catalogue) already
  // bounds a single ingest, and the reserve bounds the spend.
  catalog_items: {
    key: 'catalog_items', kind: 'gauge', addonStackable: true, unit: 'produits indexés',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: -1 },
    systemCeiling: 2_000_000,
    label: { fr: 'Produits indexés', en: 'Indexed products' }, group: 'product',
  },
  // Total tasks per tenant (all statuses). enterprise = -1 (negotiated via
  // quotaOverrides.tasks); agency = a high system-protection default, raisable
  // per-agency via quotaOverrides (an override REPLACES the default and is
  // clamped to systemCeiling, so the ceiling must stay ≥ any agency value).
  tasks: {
    key: 'tasks', kind: 'gauge', addonStackable: true, unit: 'tâches',
    // Retired as a commercial limit (pricing v2, 2026-11-04) → -1 clamps to the ceiling.
    defaultByTier: { basic: -1, pro: -1, elite: -1, enterprise: -1, agency: 10000 },
    systemCeiling: 10000, label: { fr: 'Tâches', en: 'Tasks' }, group: 'tasks',
  },

  // ---- Cibles (Targets) — two gauges with deliberately different scopes ----
  //
  // `establishments` is TENANT-WIDE: every establishment costs a Geoapify credit
  // from a 3 000/day platform-wide budget shared across all tenants. The cap is on
  // geocoding spend, not on matcher memory. Default 100 on every tier; raisable per
  // tenant via quotaOverrides.establishments (negotiated, told us in advance). D34.
  establishments: {
    key: 'establishments', kind: 'gauge', addonStackable: false, unit: 'établissements',
    defaultByTier: { basic: 100, pro: 100, elite: 100, enterprise: 100, agency: 100 },
    // Commercial ceiling — set well above any value we would negotiate.
    systemCeiling: 50_000,
    label: { fr: 'Établissements', en: 'Establishments' }, group: 'targets',
  },
  // `target_signals` is PER BRAND — the ONLY per-brand gauge in the registry.
  //
  // ⚠ NEVER sum target_signals across brands. One index is loaded per brand in
  // the search Lambda (D39); a tenant-wide pool would give an agency's 15 brands
  // 33k signals each, which destroys the feature. The per-brand cap matches the
  // one container allocation it actually protects. D13 + docs/billing/metering-and-entitlements.md §12.9.
  //
  // systemCeiling is an ENGINEERING number, not commercial: what a container can
  // actually load. Set from Lot 3's load test; ~40 bytes/signal × 500k = ~20 MB.
  // Raising it past the measured limit is a Lambda-memory change, not an admin toggle.
  //
  // The admin quota view MUST show the MAX across the tenant's brands, not a sum
  // (D13). usedByMetric in metering.controller.ts handles this specially.
  target_signals: {
    key: 'target_signals', kind: 'gauge', addonStackable: false, unit: 'signaux',
    defaultByTier: { basic: 500_000, pro: 500_000, elite: 500_000, enterprise: 500_000, agency: 500_000 },
    // Engineering ceiling — set from Lot 3 load test. Placeholder pending measurement.
    systemCeiling: 2_000_000,
    label: { fr: 'Signaux cibles', en: 'Target signals' }, group: 'targets',
  },

  // ---------------- allowlist (set membership) ----------------
  engines: {
    key: 'engines', kind: 'allowlist', addonStackable: true, unit: 'modèles',
    // Prompt Studio C17.1 (2026-10-15): per-tier allowlists drive both the Prompt
    // Studio picker and the engine resolver (engine-roster.ts).  The `defaultByTier`
    // counts are the cardinalities of the allowlistDefaults arrays.
    // enterprise / agency include all secondaries (8 = 4 + 4); max 10 via override.
    defaultByTier: { basic: 2, pro: 4, elite: 7, enterprise: 10, agency: 8 },
    allowlistDefaults: {
      basic: ENGINES_BASIC,
      pro:   ENGINES_PRO,
      elite: ENGINES_ELITE,
      enterprise: ALL_ENGINES_WITH_SECONDARY, // up to 10 customizable via override
      agency: ALL_ENGINES_WITH_SECONDARY,
    },
    systemCeiling: 10, label: { fr: 'Modèles IA', en: 'AI models' }, group: 'brand_perception',
  },

  // ---------------- scalars (per-action values) ----------------
  // Run size of a website / Earned Media audit when the brand has no « Plafond de
  // coût » (brand.auditConfig.maxPagesPerWebsiteAudit / maxFetchPerEarnedMedia,
  // 1..100, which always wins). Pricing v2 (2026-11-04): NOT a plan limit any
  // more — the run is paid in credits on every tier, so one default of 30 on
  // every non-agency tier (never shown as a plan feature); agency stays -1 = the
  // agent's full 100 budget.
  pages_per_website_audit: {
    key: 'pages_per_website_audit', kind: 'scalar', addonStackable: false, unit: 'pages',
    defaultByTier: { basic: 30, pro: 30, elite: 30, enterprise: 30, agency: -1 },
    systemCeiling: 500, label: { fr: 'Pages / audit site', en: 'Pages / website audit' }, group: 'website',
  },
  fetch_per_earned_media: {
    key: 'fetch_per_earned_media', kind: 'scalar', addonStackable: false, unit: 'fetch',
    defaultByTier: { basic: 30, pro: 30, elite: 30, enterprise: 30, agency: -1 },
    systemCeiling: 300, label: { fr: 'Fetch / earned media', en: 'Fetch / earned media' }, group: 'earned_media',
  },
  data_retention_days: {
    key: 'data_retention_days', kind: 'scalar', addonStackable: false, unit: 'jours',
    defaultByTier: { basic: 90, pro: 180, elite: 365, enterprise: -1, agency: 365 },
    systemCeiling: 1095, label: { fr: 'Conservation des données', en: 'Data retention' }, group: 'billing',
  },

  // ---------------- meters (flow — per period / per balance) ----------------
  // RETIRED 2026-11-04 (pricing v2): content creation (news_watch) is paid in
  // credits on every tier — nothing asserts this meter any more. Kept so stored
  // overrides stay readable.
  content_creations: {
    key: 'content_creations', kind: 'meter', period: 'month', addonStackable: true, unit: 'créations',
    defaultByTier: { basic: -1, pro: -1, elite: -1, enterprise: -1, agency: -1 },
    systemCeiling: 1000, label: { fr: 'Créations de contenu', en: 'Content creations' }, group: 'website',
  },
  // Product module — OPTIMIZATION REVIEWS: one unit per catalogue item (product
  // OR category page) pushed through the optimize pipeline. A MONTHLY balance
  // that resets like content_creations, because the cost is per LLM run: a
  // RE-optimization costs exactly the same as the first one (same fetch + same
  // Nemotron call), so it consumes a fresh unit rather than being free.
  //
  // Consumed by the whole run at once at optimize.start (the run size is known
  // up front), so the gate is `used + itemCount > limit` — a run that doesn't
  // fit is rejected whole rather than half-billed. Agency is exempt (-1): it
  // pays in credits via the reserve. Every self-serve tier defaults to 0 —
  // capacity is bought as a pack alongside catalog_items, or negotiated
  // (quotaOverrides.product_reviews). Enterprise is 0 for the same reason
  // catalog_items is: the module is unlocked per deal, so BOTH numbers are set
  // explicitly at tenant creation — an unset override must not mean "unlimited".
  product_reviews: {
    key: 'product_reviews', kind: 'meter', period: 'month', addonStackable: true, unit: 'optimisations',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: -1 },
    systemCeiling: 2_000_000,
    label: { fr: 'Optimisations produits', en: 'Product optimizations' }, group: 'product',
  },
  // The unit of credit consumption — every tier since pricing v2 (2026-11-04):
  // agency pays everything in credits; other tiers pay website audits, Earned
  // Media and content creation (`isCreditMetered`, credits.ts). Granted via
  // Stripe Price metadata (+ the monthly grant job for non-agency), never a
  // tier default, so every tier defaults to 0.
  credit: {
    key: 'credit', kind: 'meter', period: 'balance', addonStackable: true, unit: 'crédits',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: 0 },
    systemCeiling: -1, label: { fr: 'Crédits', en: 'Credits' }, group: 'billing',
  },

  // ---------------- booleans (feature on/off) ----------------
  // Pricing v2 D4 (2026-11-04): MCP + public API on every tier (was elite+). Display
  // boolean for the plan cards; the real gate is TierLimits.webhookAccess.
  api_access: {
    key: 'api_access', kind: 'boolean', addonStackable: false, unit: '',
    defaultByTier: { basic: 1, pro: 1, elite: 1, enterprise: 1, agency: 1 },
    systemCeiling: 1, label: { fr: 'Accès API / MCP', en: 'API / MCP access' }, group: 'billing',
  },
  // « Service client dédié » (CSM) — Elite and up. Display only, no code gate.
  premium_support: {
    key: 'premium_support', kind: 'boolean', addonStackable: false, unit: '',
    defaultByTier: { basic: 0, pro: 0, elite: 1, enterprise: 1, agency: 1 },
    systemCeiling: 1, label: { fr: 'Service client dédié', en: 'Dedicated customer success' }, group: 'billing',
  },
  news_watch_proposals: {
    key: 'news_watch_proposals', kind: 'scalar', addonStackable: false, unit: 'propositions',
    defaultByTier: { basic: 1, pro: 3, elite: 5, enterprise: 5, agency: -1 },
    systemCeiling: 50, label: { fr: 'Propositions News Watch', en: 'News Watch proposals' }, group: 'news_watch',
  },
  reports: {
    key: 'reports', kind: 'boolean', addonStackable: false, unit: '',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 0, agency: 1 },
    systemCeiling: 1, label: { fr: 'Rapports', en: 'Reports' }, group: 'billing',
  },

  // ---- Conversations (D60, Lot 1) — beta-gated by `conversationsEnabled` flag ----
  //
  // `conversation_personas` is a TENANT-WIDE gauge (all brands pooled).  The per-brand
  // structural cap (CONVERSATION_MAX_PERSONAS_PER_BRAND = 50) is enforced separately in
  // ConversationsService as a constant — deliberately NOT a per-brand gauge entry here
  // (per-brand gauges carry a real cost on every admin screen; `target_signals` and
  // `brand_personas` are the only two).
  //
  // basic = 0 is the commercial gate: the `requireQuota: 'conversation_personas'` sidebar
  // item rule hides the nav item when the limit resolves to 0.  Raisable per tenant via
  // quotaOverrides; enterprise/agency defaults are a runaway cap, not a commercial limit.
  conversation_personas: {
    key: 'conversation_personas', kind: 'gauge', addonStackable: true, unit: 'personas',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 200, agency: 200 },
    systemCeiling: 10_000,
    label: { fr: 'Personas de conversation', en: 'Conversation personas' },
    group: 'conversations',
  },
  // `brand_personas` is PER BRAND — the second per-brand gauge after `target_signals`
  // (2026-10-11). A Persona (`BrandPersona`, Brand Knowledge « Leviers de mesure » tab) is
  // the reusable « Qui est-il ? » card a conversation links to. Counted per brand because a
  // persona is brand content (an agency's brands must not starve each other) and the
  // conversation editor loads one brand's list in full. ⚠ Never sum across brands — the
  // admin quota view shows the MAX, like `target_signals`. Overridable via quotaOverrides.
  brand_personas: {
    key: 'brand_personas', kind: 'gauge', addonStackable: false, unit: 'personas',
    defaultByTier: { basic: 100, pro: 100, elite: 100, enterprise: 100, agency: 100 },
    systemCeiling: 500,
    label: { fr: 'Personas (par marque)', en: 'Personas (per brand)' },
    group: 'conversations',
  },
  // ---- Prompt Studio conversations (C18, 2026-10-15) -----------------------
  // PER-BRAND monthly allowance: 1 conversation × 1 language × 1 place × 1 engine
  // × its Runs per launch.  `conversationRuns === 0` hides the Conversations tab
  // and all Couverture conversation counts in the UI.  Read ONLY through
  // `resolveBrandAllowances(tenant, brand)`.  ⚠ Never sum across brands.
  // A trial tenant = basic (0 = tab hidden).  Finite values for self-serve tiers
  // are a prerequisite of lot I2's deploy (lot A step 2b sets them).
  brand_conversation_runs: {
    key: 'brand_conversation_runs', kind: 'scalar', addonStackable: false, unit: 'runs',
    // Enterprise = Elite default (pricing v2, 2026-11-04), never -1.
    defaultByTier: { basic: 0, pro: 0, elite: 150, enterprise: 150, agency: -1 },
    systemCeiling: -1,
    label: { fr: 'Runs de conversation par mois (par marque)', en: 'Conversation runs per month (per brand)' },
    group: 'conversations',
  },
  // Lot I2 (owner 2026-10-30, step 5b): conversations stop reading this — runs per conversation
  // are unlimited; the money bound is `brand_conversation_runs` above, and the structural ceilings are
  // CONVERSATION_MAX_RUNS_PER_CELL (20 per cell) and CONVERSATION_MAX_RUNS (5 000 per launch),
  // checked at save, never per tier. Kept in the registry (stored overrides stay readable).
  runs_per_conversation: {
    key: 'runs_per_conversation', kind: 'scalar', addonStackable: false, unit: 'runs',
    defaultByTier: { basic: 0, pro: 0, elite: 0, enterprise: 20, agency: 20 },
    systemCeiling: 20,
    label: { fr: 'Runs par conversation', en: 'Runs per conversation' },
    group: 'conversations',
  },
  // Maximum turns per conversation (platform ceiling = 6).
  turns_per_conversation: {
    key: 'turns_per_conversation', kind: 'scalar', addonStackable: false, unit: 'tours',
    defaultByTier: { basic: 0, pro: 5, elite: 5, enterprise: 6, agency: 6 },
    systemCeiling: 6,
    label: { fr: 'Tours par conversation', en: 'Turns per conversation' },
    group: 'conversations',
  },
  /**
   * D56 — tenant-wide daily turn circuit breaker, checked in the sweep on EVERY tier.
   * ADD-ed at credit commit (not reserve), read once per sweep per tenant.
   * Over-cap → persona takes the quota-block path: one deduplicated notification,
   * ConversationSweepBlocked{reason:'daily_turns'}, nextRunAt advanced, no engine calls.
   * Credits bound agency only; this guardrail binds every tier so a mis-set override
   * costs one day instead of one month.
   * NOTE: kind is 'meter' so it resets (day period), not a gauge.
   */
  conversation_turns_daily: {
    key: 'conversation_turns_daily', kind: 'meter', period: 'day', addonStackable: false, unit: 'tours',
    defaultByTier: { basic: 0, pro: 200, elite: 800, enterprise: 20000, agency: 40000 },
    systemCeiling: 100000,
    label: { fr: 'Tours par jour', en: 'Turns per day' },
    group: 'conversations',
  },
};

/**
 * One row of the tenant usage summary (GET /metering/usage). `limit === -1`
 * means unlimited; `used` is the live count; `label`/`unit` come from the
 * registry so the UI doesn't re-derive them.
 */
export interface UsageMetric {
  metric: string;
  used: number;
  limit: number;
  label: { fr: string; en: string };
  unit: string;
}

/**
 * Tenant-level quota usage summary surfaced on Settings → Marques. Tenant-wide
 * gauges only (brands/zones/languages/prompts/competitors/users/tasks). Credits
 * (agency balance) + per-brand cadence are returned alongside via their own
 * shapes by the controller, not in this list.
 */
export interface MeteringUsageSummary {
  tier: string;
  metrics: UsageMetric[];
}

/**
 * Per-brand feature usage — surfaced as professional cards for AGENCY tenants
 * (which have no commercial quota to display, just what each brand uses). The
 * counts are pure usage (no limit), and cadence is the effective per-axis
 * schedule. Tier-display tenants use `MeteringUsageSummary` instead.
 */
export interface BrandUsage {
  brandId: string;
  brandName: string;
  url?: string;
  prompts: number;
  zones: number;
  languages: number;
  competitors: number;
  tasks: number;
  /**
   * Cibles signals held by THIS brand. The only per-brand gauge (D13) — the tenant-wide
   * `target_signals` row in `metrics` carries the MAX of these, never their sum, so this
   * is the number the admin UI must show next to the cap.
   */
  targetSignals?: number;
  /** Cibles établissements on this brand; the `establishments` metric sums them (D34). */
  establishments?: number;
  /** Effective cadence per schedulable axis (brand override → tier default). */
  cadence: Record<string, Cadence>;
  /**
   * The brand's ONE prompt cadence (`brandPromptCadence`: PSCHED off 'legacy'). The `brand_*`
   * entries of `cadence` are the legacy per-axis overrides and no longer what runs.
   */
  promptCadence?: Cadence;
  /** The brand conversation cadence (`resolveConversationCadence`); absent without `conversationsEnabled`. */
  conversationCadence?: Cadence;
  /** Non-archived conversations (personas) on this brand; absent without `conversationsEnabled`. */
  conversations?: number;
  /** Σ `personaRunsPerLaunch` over those conversations; absent without `conversationsEnabled`. */
  conversationRunsPerLaunch?: number;
  /**
   * ISO 639-1 codes the brand actually measures: active prompts' languages (unlabelled = working
   * language) ∪ its conversations' cell languages. Working language first. Distinct from
   * `languages` (the count of `strategy.targetLanguages`, the gauge).
   */
  usedLanguages?: string[];
}

/**
 * A resolved effective limit for one tenant × one metric. Computed, never stored.
 * `used`/`reserved`/`available` are filled by the metering service at read time
 * (left undefined by the pure resolver).
 */
export interface Entitlement {
  metric: string;
  kind: MetricKind;
  limit: number;
  allowed?: string[];
  used?: number;
  reserved?: number;
  available?: number;
}

const UNLIMITED = -1;

/** Clamp `raw` to a ceiling, honoring -1 (unlimited) on both sides. */
function clampToCeiling(raw: number, ceiling: number): number {
  if (raw === UNLIMITED && ceiling === UNLIMITED) return UNLIMITED;
  if (raw === UNLIMITED) return ceiling; // unlimited request, finite ceiling → ceiling
  if (ceiling === UNLIMITED) return raw;
  return Math.min(raw, ceiling);
}

/**
 * Resolve the effective limit for one metric. Resolution order (doc §4.1):
 *
 *   base     = registry.defaultByTier[tier]
 *   packs    = Σ active addon packs for this metric (additive, only if addonStackable)
 *   override = quotaOverrides[metric] — if set, REPLACES base+packs (negotiated truth)
 *   raw      = override ?? (base === -1 ? -1 : base + packs)
 *   limit    = min(raw, systemCeiling)   // ceiling always clamps (invariant #5)
 *
 * Unknown metric → throws (programmer error). Unknown tier → falls back to
 * `basic` semantics by reading defaultByTier['basic'] (the resolver never throws
 * on tier, mirroring resolveLimits — it can run in always-rendered UI).
 */
export function resolveEntitlement(
  metric: string,
  tier: string,
  opts?: { packs?: number; override?: number },
): Entitlement {
  const def = METRIC_REGISTRY[metric];
  if (!def) {
    throw new Error(`Unknown metric: ${metric}`);
  }

  const base = def.defaultByTier[tier as Tier] ?? def.defaultByTier.basic;
  const packs = def.addonStackable ? opts?.packs ?? 0 : 0;
  const override = opts?.override;

  const raw =
    override !== undefined
      ? override
      : base === UNLIMITED
        ? UNLIMITED
        : base + packs;

  const limit = clampToCeiling(raw, def.systemCeiling);

  const entitlement: Entitlement = { metric, kind: def.kind, limit };

  if (def.kind === 'allowlist') {
    entitlement.allowed = def.allowlistDefaults?.[tier as Tier] ?? def.allowlistDefaults?.basic ?? [];
  }

  return entitlement;
}

/** Resolve every registry metric for a tenant at once. */
export function resolveAllEntitlements(
  tier: string,
  packsByMetric?: Record<string, number>,
  overridesByMetric?: Record<string, number>,
): Record<string, Entitlement> {
  const out: Record<string, Entitlement> = {};
  for (const metric of Object.keys(METRIC_REGISTRY)) {
    out[metric] = resolveEntitlement(metric, tier, {
      packs: packsByMetric?.[metric],
      override: overridesByMetric?.[metric],
    });
  }
  return out;
}

// ───────────────── Stripe entitlement reconstruction (doc §4.2) ─────────────────
//
// Rebuild tier + quota-extension packs from a Stripe subscription's items. The
// hard rule: every quota-granting Stripe object carries `metadata.{metric,amount}`
// (and the base tier item carries `metadata.tier`) — set by setup-stripe-catalog.ts.
// Pure: callers pass a minimal subscription shape (no Stripe SDK dep in shared).

/** Minimal shape of a Stripe subscription item we read. */
export interface StripeSubItemLike {
  quantity?: number | null;
  price?: { metadata?: Record<string, string> | null } | null;
}
export interface StripeSubLike {
  items?: { data?: StripeSubItemLike[] } | null;
}

/** The base tier carried by any item's price `metadata.tier` (undefined for inline price_data). */
export function priceToTier(sub: StripeSubLike): string | undefined {
  for (const item of sub.items?.data ?? []) {
    const t = item.price?.metadata?.tier;
    if (t) return t;
  }
  return undefined;
}

/**
 * Sum quota-extension packs from a subscription's items into a
 * `{ metric: amount }` map. An item is a pack when its price metadata carries
 * `metric` + numeric `amount`; multiple items for the same metric stack, and
 * `amount` is multiplied by the item `quantity`. The base tier item (metric
 * absent) is ignored here. Only `addonStackable` metrics are kept — a stray
 * pack on a non-stackable metric is dropped (defensive).
 */
export function packsFromSubscription(sub: StripeSubLike): Record<string, number> {
  const packs: Record<string, number> = {};
  for (const item of sub.items?.data ?? []) {
    const meta = item.price?.metadata;
    if (!meta?.metric || meta.amount === undefined) continue;
    const def = METRIC_REGISTRY[meta.metric];
    if (!def || !def.addonStackable) continue;
    const amount = Number(meta.amount);
    if (!Number.isFinite(amount)) continue;
    const qty = item.quantity ?? 1;
    packs[meta.metric] = (packs[meta.metric] ?? 0) + amount * qty;
  }
  return packs;
}

/**
 * Reconstruct a tenant's full entitlement set from Stripe (tier + packs) plus
 * any DDB quota overrides (negotiated enterprise limits, NOT reconstructable
 * from Stripe). When the subscription has no catalog tier (enterprise/agency
 * inline price_data), pass `fallbackTier` (the tenant's stored tier).
 *
 * NOTE: the agency `credit` meter is granted via invoice.paid + top-ups (a
 * BALANCE, not a limit), so an agency `credit` pack item here is intentionally
 * NOT turned into a limit — credit packs flow through CreditService, not this.
 */
export function entitlementsFromStripe(
  sub: StripeSubLike,
  fallbackTier: string,
  overridesByMetric?: Record<string, number>,
): { tier: string; packsByMetric: Record<string, number>; entitlements: Record<string, Entitlement> } {
  const tier = priceToTier(sub) ?? fallbackTier;
  const packsByMetric = packsFromSubscription(sub);
  // credit is a balance, not a stackable limit — never let a credit pack inflate
  // a `credit` entitlement "limit".
  delete packsByMetric.credit;
  return {
    tier,
    packsByMetric,
    entitlements: resolveAllEntitlements(tier, packsByMetric, overridesByMetric),
  };
}
